import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Truck, Loader2, ChevronDown, ArrowRight } from 'lucide-react';
import { storeApi } from '../../lib/api';
import { formatAUD } from './CartContext';
import { useDeliveryQuote } from './useDeliveryQuote';

function ZoneList({ zones }) {
  const [open, setOpen] = useState(null);
  return (
    <ul className="space-y-2" data-testid="delivery-zone-list">
      {zones.map((z) => (
        <li key={z.id} className="bg-white rounded-xl" style={{ border: '1px solid var(--s-sand)' }}>
          <button type="button" onClick={() => setOpen(open === z.id ? null : z.id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm text-left" data-testid={`zone-item-${z.id}`}>
            <span className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4" style={{ color: 'var(--s-green-3)' }} /> {z.name}</span>
            <span className="flex items-center gap-2 font-heading font-bold" style={{ color: 'var(--s-green)' }}>
              {formatAUD(z.fee)}{z.free_over_m2 != null && <span className="text-[11px] font-medium" style={{ color: 'var(--s-muted)' }}>free over {z.free_over_m2} m²</span>}
              <ChevronDown className={`h-4 w-4 transition-transform ${open === z.id ? 'rotate-180' : ''}`} />
            </span>
          </button>
          {open === z.id && <p className="px-4 pb-3 text-xs leading-relaxed" style={{ color: 'var(--s-muted)' }}>{z.suburbs.join(' · ')}</p>}
        </li>
      ))}
    </ul>
  );
}

export function DeliveryChecker() {
  const [suburb, setSuburb] = useState('');
  const [zones, setZones] = useState([]);
  const { quote, checking, hasInput } = useDeliveryQuote({ suburb, postcode: /^\d{4}$/.test(suburb.trim()) ? suburb.trim() : '', totalM2: 0, enabled: true });

  useEffect(() => { storeApi.getDeliveryZones().then((r) => setZones(r.data)).catch(() => {}); }, []);

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20 lg:pb-28" data-testid="delivery-checker">
      <div className="rounded-3xl p-8 lg:p-12 grid lg:grid-cols-2 gap-10" style={{ background: 'var(--s-sand)' }}>
        <div>
          <p className="store-eyebrow mb-3">Delivery</p>
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">Do we deliver to you?</h2>
          <p className="mb-6 leading-relaxed" style={{ color: 'var(--s-muted)' }}>Type your suburb or postcode to see the delivery price up front. Pickup from the farm is always free.</p>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--s-muted)' }} />
            <input value={suburb} onChange={(e) => setSuburb(e.target.value)} placeholder="e.g. Beaudesert or 4285" className="store-input" style={{ paddingLeft: '2.75rem', paddingTop: '1rem', paddingBottom: '1rem', fontSize: '1rem' }} data-testid="delivery-check-input" />
            {checking && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" style={{ color: 'var(--s-muted)' }} />}
          </div>
          {hasInput && !checking && (
            <div className={`mt-4 rounded-xl px-5 py-4 ${quote?.matched ? 'bg-white' : 'bg-white/60'}`} data-testid="delivery-check-result">
              {quote?.matched ? (
                <>
                  <p className="font-heading font-bold text-lg" style={{ color: 'var(--s-green)' }}>Yes! {quote.zone_name} — {formatAUD(quote.base_fee)} delivery</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--s-muted)' }}>{quote.free_over_m2 != null ? `Free delivery on orders of ${quote.free_over_m2} m² or more. ` : ''}The price is added to your order automatically.</p>
                  <Link to="/shop/order" className="inline-flex items-center gap-1 text-sm font-semibold mt-3" style={{ color: 'var(--s-terra)' }} data-testid="delivery-check-order-link">Start your order <ArrowRight className="h-4 w-4" /></Link>
                </>
              ) : (
                <>
                  <p className="font-heading font-bold" style={{ color: 'var(--s-ink)' }}>Not in a set zone yet</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--s-muted)' }}>We likely still deliver — place your order and we'll confirm a price by phone, or collect from the farm for free.</p>
                </>
              )}
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: 'var(--s-muted)' }}>Delivery zones</p>
          {zones.length === 0 ? <p className="text-sm" style={{ color: 'var(--s-muted)' }}>Delivery is quoted when we confirm your order.</p> : <ZoneList zones={zones} />}
        </div>
      </div>
    </section>
  );
}
